/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { platformApi } from './api';

// Bare-metal order types
export interface BaremetalOrderRequest {
  project_id: string;
  cpu: number;
  ram: number;
  storage: number;
  storage_type: string;
  gpu?: string;
  gpu_count?: number;
  location: string;
  os: string;
  duration: number; // months
  notes?: string;
  // User preferences
  server_name: string;
  ssh_key_id?: string;
  bandwidth_tier?: 'standard' | 'premium' | 'enterprise';
  backup_enabled?: boolean;
  monitoring_enabled?: boolean;
  // Contract & Pricing
  contract_type?: 'on_demand' | 'reserved';
  commitment_period?: number; // 3, 6, or 12 months
  auto_renew?: boolean;
  // Advanced Configuration
  disk_layouts?: DiskLayout[];
  cloud_init_config?: string;
  // Contact information
  technical_contact_name?: string;
  technical_contact_email?: string;
  technical_contact_phone?: string;
  company_name?: string;
}

export interface DiskLayout {
  disk_type: string;  // NVME, SSD, HDD
  disk_count: number;
  size_per_disk_gb: number;
  layout: string;     // lvm, raid0, raid1, single
  format: string;     // ext4, xfs, btrfs
  mountpoint: string; // /data0, /data1, etc.
}

export interface BaremetalOrder {
  id: string;
  order_id: string;
  user_id: string;
  project_id: string;
  cpu: number;
  ram: number;
  storage: number;
  storage_type: string;
  gpu?: string;
  gpu_count?: number;
  location: string;
  os: string;
  duration: number;
  notes?: string;
  monthly_price_inr: number;
  total_price_inr: number;
  status: string;
  payment_status: string;
  payment_method?: string;
  paid_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CheckoutRequest {
  use_wallet_balance: boolean;
  wallet_amount?: number;
  payment_method: 'razorpay' | 'paypal' | 'binance' | 'wallet_only';
}

export interface CheckoutResponse {
  order_id: string;
  total_amount: number;
  wallet_balance: number;
  wallet_amount_used: number;
  amount_to_pay: number;
  payment_method: string;
  // Razorpay
  razorpay_order_id?: string;
  razorpay_key?: string;
  // PayPal
  paypal_order_id?: string;
  paypal_approval_url?: string;
  // Binance
  binance_checkout_url?: string;
  binance_qr_code?: string;
}

export interface BaremetalInstance {
  id: string;
  order_id: string;
  status: string;
  cpu: number;
  ram: number;
  storage: number;
  storage_type: string;
  gpu?: string;
  gpu_count?: number;
  location: string;
  os: string;
  duration: number;
  server_ip?: string;
  server_hostname?: string;
  ssh_port?: number;
  ssh_username?: string;
  ssh_password?: string;
  root_password?: string;
  ipmi_ip?: string;
  ipmi_username?: string;
  ipmi_password?: string;
  console_url?: string;
  vendor_name?: string;
  vendor_order_id?: string;
  vendor_contact?: string;
  actual_cpu?: number;
  actual_ram?: number;
  actual_storage?: number;
  actual_gpu?: string;
  actual_gpu_count?: number;
  monthly_price_inr: number;
  total_price_inr: number;
  provisioned_at?: string;
  expires_at?: string;
  created_at: string;
}

export interface BaremetalInstancesResponse {
  instances: BaremetalInstance[];
  total: number;
}

export interface OrderStatusResponse {
  order_id: string;
  status: string;
  status_message?: string;
  paid_at?: string;
  provisioned_at?: string;
  expires_at?: string;
  // Order details
  cpu: number;
  ram: number;
  storage: number;
  storage_type: string;
  gpu?: string;
  gpu_count?: number;
  location: string;
  os: string;
  duration: number;
  monthly_price_inr: number;
  total_price_inr: number;
  payment_status: string;
  created_at: string;
}

export interface ReleaseInstanceRequest {
  reason?: string;
}

export interface PricingResponse {
  monthly_price_inr: number;
  total_price_inr: number;
  commitment_discount: number;
  duration: number;
  commitment_period: number;
  contract_type: string;
}

// Bare-metal API service
export const baremetalApi = {
  // Calculate pricing for a configuration
  calculatePricing: (data: BaremetalOrderRequest) =>
    platformApi.post<PricingResponse>('/api/v1/baremetal/calculate-pricing', data, {
      headers: { 'X-Project-Id': data.project_id }
    }),

  // Place a new bare-metal order
  placeOrder: (data: BaremetalOrderRequest) =>
    platformApi.post<BaremetalOrder>('/api/v1/baremetal/orders', data, {
      headers: { 'X-Project-Id': data.project_id }
    }),

  // Proceed to checkout with payment options
  checkout: (orderId: string, projectId: string, data: CheckoutRequest) =>
    platformApi.post<CheckoutResponse>(`/api/v1/baremetal/orders/${orderId}/checkout`, data, {
      headers: { 'X-Project-Id': projectId }
    }),

  // Confirm payment (called after successful gateway payment)
  confirmPayment: (orderId: string, projectId: string, data: {
    payment_id: string;
    transaction_id: string;
    payment_method: string;
  }) =>
    platformApi.post(`/api/v1/baremetal/orders/${orderId}/confirm-payment`, data, {
      headers: { 'X-Project-Id': projectId }
    }),

  // Get order status
  getOrderStatus: (orderId: string, projectId: string) =>
    platformApi.get<OrderStatusResponse>(`/api/v1/baremetal/orders/${orderId}/status`, {
      headers: { 'X-Project-Id': projectId }
    }),

  // List user's bare-metal instances
  listInstances: (projectId: string) =>
    platformApi.get<BaremetalInstancesResponse>('/api/v1/baremetal/instances', {
      headers: { 'X-Project-Id': projectId }
    }),

  // Get instance details
  getInstanceDetails: (orderId: string, projectId: string) =>
    platformApi.get<BaremetalInstance>(`/api/v1/baremetal/instances/${orderId}`, {
      headers: { 'X-Project-Id': projectId }
    }),

  // Release instance
  releaseInstance: (orderId: string, projectId: string, data?: ReleaseInstanceRequest) =>
    platformApi.post(`/api/v1/baremetal/instances/${orderId}/release`, data || {}, {
      headers: { 'X-Project-Id': projectId }
    }),
};
